import React, { useMemo, useCallback } from 'react';

import type { Channel } from 'application-types';
import {
  Edge as BuildingEdge,
} from 'lane-shared/contexts/SignUpContext/useOnboardingBuildings';
import { Dropdown, DropdownItem } from 'design-system-web';
import styles from './index.scss';
import { ChannelListItemWrapper, convertBuildingEdgeToChannelType, convertChannelToBuildingEdge } from 'components/onboarding/components/ChannelListItemWrapper/ChannelListItemWrapper';


type OnboardingBuildingDropDownInputProps = {
  data: {
    isReadOnly: boolean;
    building: Channel | null;
    buildings: BuildingEdge[];
  };
  config: {
    isLoading: boolean;
  };
  handlers: {
    onBuildingSelectionChange: (building: Channel | null) => void;
  };
};

export function OnboardingBuildingDropDownInput({
  data: { isReadOnly, building, buildings },
  config: { isLoading },
  handlers: { onBuildingSelectionChange },
}: OnboardingBuildingDropDownInputProps) {

  // Convert buildings to DropdownItem format
  const dropdownItems = useMemo<DropdownItem<BuildingEdge>[]>(
    () =>
      buildings.map(b => ({
        label: b.node.name, // Required by react-select
        value: b,
      })),
    [buildings]
  );

  // Find selected item in dropdown format
  const selectedDropdownItem = useMemo<DropdownItem<BuildingEdge> | null>(() => {
    if (!building) {
      return null;
    }

    // Try to find the building in the array first
    const selectedBuildingEdge = buildings.find(b => b.node.id === building._id);
    
    if (selectedBuildingEdge) {
      return {
        label: selectedBuildingEdge.node.name,
        value: selectedBuildingEdge,
      };
    }

    // If not found, convert Channel to BuildingEdge using helper function
    const buildingEdge = convertChannelToBuildingEdge(building);
    const buildingName: string = building.profile?.name || '';

    return {
      label: buildingName,
      value: buildingEdge,
    };
  }, [building, buildings]);

  // Handle selection change
  const handleDropdownChange = useCallback(
    (item: DropdownItem<BuildingEdge> | null) => {
      if (item) {
        const channel = convertBuildingEdgeToChannelType(item.value);

        onBuildingSelectionChange(channel);
      } else {
        onBuildingSelectionChange(null);
      }
    },
    [onBuildingSelectionChange]
  );

  // Load options - triggers search when user types
  const loadOptions = useCallback(
    async (inputValue: string): Promise<DropdownItem<BuildingEdge>[]> => {
      // Update search text - this triggers the search query
      // onBuildingSearchTextChange(inputValue); //TODO: Uncomment this once we have a proper search with infinite scroll
      
      // Filter buildings based on input value (case-insensitive)
      const filteredBuildings = inputValue
        ? buildings.filter(b =>
            b.node.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            b.node.address?.street1?.toLowerCase().includes(inputValue.toLowerCase())
          )
        : buildings;
      
      // Return filtered buildings as dropdown items
      return filteredBuildings.map(b => ({
        label: b.node.name,
        value: b,
      }));
    },
    [buildings]
  );

  // Custom option rendering - adapts renderItem to renderOption
  const renderOption = useCallback(
    (props: any) => {
      const buildingItem = props.data.value;
      const isSelected = building?._id === buildingItem.node.id;

      return (
        <ChannelListItemWrapper
          building={buildingItem}
          onClick={(_channel: Channel) => {
            props.selectOption(props.data);
          }}
          isSelected={Boolean(building && isSelected)}
          className={styles.buildingResult}
        />
      );
    },
    [building]
  );

  // Custom selected value rendering
  const renderSelectedValue = useCallback(
    (props: any) => {
      if (!props.data) return null;

      const buildingItem = props.data.value;

      return <span>{buildingItem.node.name}</span>;
    },
    []
  );

  return (
    <div className={styles.inputWrapper}>
      <Dropdown<BuildingEdge>
        value={selectedDropdownItem}
        onChange={handleDropdownChange}
        loadOptions={loadOptions}
        defaultOptions={dropdownItems}
        renderOption={renderOption}
        renderSelectedValue={renderSelectedValue}
        isLoading={isLoading}
        placeholder="onboarding.swire.location.buildingLocationPlaceholder"
        isSearchable
        disabled={isReadOnly}
        testId="buildingLocation"
        className={isReadOnly ? styles.disabledDropdown : ''}
      />
    </div>
  );
}
